import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/queries/query-keys";

export interface Education {
  id: number;
  institution: string;
  degree: string;
  period: string;
  notes: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateEducationInput {
  institution: string;
  degree: string;
  period: string;
  notes?: string;
  display_order?: number;
}

// Fetch all education (public)
export function useEducation() {
  return useQuery({
    queryKey: queryKeys.education.all,
    queryFn: async (): Promise<Education[]> => {
      const response = await fetch("/api/education");
      if (!response.ok) {
        throw new Error("Failed to fetch education");
      }
      const data = await response.json();
      return data.education;
    },
  });
}

// Fetch all education (admin)
export function useAdminEducation() {
  return useQuery({
    queryKey: queryKeys.education.admin,
    queryFn: async (): Promise<Education[]> => {
      const response = await fetch("/api/admin/education");
      if (!response.ok) {
        throw new Error("Failed to fetch education");
      }
      const data = await response.json();
      return data.education;
    },
  });
}

// Create education
export function useCreateEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateEducationInput) => {
      const response = await fetch("/api/admin/education", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw new Error("Failed to create education");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.education.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.education.admin });
    },
  });
}

// Update education
export function useUpdateEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: CreateEducationInput & { id: number }) => {
      const response = await fetch(`/api/admin/education/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw new Error("Failed to update education");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.education.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.education.admin });
    },
  });
}

// Delete education
export function useDeleteEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/admin/education/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete education");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.education.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.education.admin });
    },
  });
}

// Reorder education
export function useReorderEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (order: number[]) => {
      const response = await fetch("/api/admin/education/reorder", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order }),
      });
      if (!response.ok) {
        throw new Error("Failed to reorder education");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.education.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.education.admin });
    },
  });
}
